// src/pages/Links.js
// ═══════════════════════════════════════════════════════════════════════
// Phase 5.1f — Tracking Links Management Page
//
// CHANGES FROM 5.1e:
//   - Added admin-only "Check Pending" button next to existing
//     "Flush Pending Clicks" button
//   - Check Pending: polls each link's PHP file via ?ipanel_count=TOKEN
//     and shows a modal with per-link counts + total
//   - Each row in the modal has a "Flush This Link" button (selective)
//   - "Flush All" button at the bottom of the modal (calls existing endpoint)
//
// New backend endpoints used:
//   POST /api/tracking-links/count           — admin pending check (no drain)
//   POST /api/tracking-links/:id/flush       — admin selective flush
//
// Existing backend endpoints used (unchanged):
//   GET    /api/tracking-links               (list, supports pagination)
//   POST   /api/tracking-links/:id/disable
//   POST   /api/tracking-links/:id/enable
//   DELETE /api/tracking-links/:id           (admin only)
//   POST   /api/tracking-links/flush         (admin flush all)
// ═══════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "../styles/links.css";
import { edtDateTime } from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";
const PG_OPTIONS = [25, 50, 100];

// ─── Status pill ──────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    active:        { cls: "tlk-pill-green",  dot: "🟢", label: "Active" },
    disabled:      { cls: "tlk-pill-amber",  dot: "🟡", label: "Disabled" },
    soft_deleted:  { cls: "tlk-pill-red",    dot: "🔴", label: "Soft-deleted" },
  };
  const cfg = map[status] || { cls: "tlk-pill-grey", dot: "⚪", label: status || "—" };
  return <span className={`tlk-pill ${cfg.cls}`}>{cfg.dot} {cfg.label}</span>;
}

// ─── Pagination helper ────────────────────────────────────────────────
function buildPageNumbers(currentPage, totalPages) {
  const maxVisible = 5;
  if (totalPages <= maxVisible) return Array.from({length: totalPages}, (_, i) => i + 1);
  let start = Math.max(1, currentPage - 2);
  let end   = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  const out = [];
  for (let p = start; p <= end; p++) out.push(p);
  return out;
}

// ─── Clipboard ────────────────────────────────────────────────────────
function copyToClipboard(text, onCopied) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => {
    if (onCopied) onCopied(text);
  }).catch(() => {});
}

// ─── Confirmation modal ───────────────────────────────────────────────
function ConfirmModal({ title, body, confirmLabel, confirmDanger, busy, onConfirm, onClose }) {
  return (
    <div className="tlk-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tlk-mo-box" style={{ maxWidth: 460 }}>
        <div className="tlk-mo-hd">
          <span className="tlk-mo-title" style={{ color: confirmDanger ? "#DC2626" : "var(--text-primary)" }}>
            {title}
          </span>
          <button className="tlk-mo-close" onClick={onClose}>✕</button>
        </div>
        <div className="tlk-mo-body">
          <div className="tlk-mo-body-text">{body}</div>
          <div className="tlk-mo-foot" style={{ marginTop: 20 }}>
            <button className="tlk-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              className={confirmDanger ? "tlk-btn-delete" : "tlk-btn-save"}
              disabled={busy}
              onClick={onConfirm}>
              {busy ? "Working…" : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Flush Result modal (simple summary, no per-link controls) ────────
function FlushResultModal({ result, onClose }) {
  if (!result) return null;
  const hasErrors = (result.errors && result.errors.length > 0);
  const stillPending = result.still_pending || 0;

  return (
    <div className="tlk-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tlk-mo-box" style={{ maxWidth: 620 }}>
        <div className="tlk-mo-hd">
          <span className="tlk-mo-title">Flush Pending Clicks — Result</span>
          <button className="tlk-mo-close" onClick={onClose}>✕</button>
        </div>
        <div className="tlk-mo-body">
          <div style={{ marginBottom: 14, fontSize: 14, lineHeight: 1.6 }}>
            <div><strong>Scanned:</strong> {result.scanned || 0} link{result.scanned !== 1 ? "s" : ""}</div>
            <div style={{ color: "#16A34A" }}><strong>Recovered:</strong> {result.flushed || 0} click{result.flushed !== 1 ? "s" : ""}</div>
            {stillPending > 0 && (
              <div style={{ color: "#D97706" }}>
                <strong>Still pending:</strong> {stillPending} (will retry on next button click)
              </div>
            )}
            <div style={{ color: "var(--text-muted)" }}><strong>Duration:</strong> {result.duration_ms || 0}ms</div>
          </div>

          {hasErrors && (
            <details style={{ marginTop: 14 }}>
              <summary style={{ cursor: "pointer", fontWeight: 600, color: "#DC2626" }}>
                {result.errors.length} error{result.errors.length !== 1 ? "s" : ""} (click to expand)
              </summary>
              <div style={{
                marginTop: 8, padding: 10,
                background: "var(--bg-input, #f9fafb)",
                border: "1px solid var(--border-color, #e5e7eb)",
                borderRadius: 6, maxHeight: 200, overflow: "auto",
                fontSize: 12, fontFamily: "monospace",
              }}>
                {result.errors.map((e, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>{e}</div>
                ))}
              </div>
            </details>
          )}

          {!hasErrors && (result.flushed || 0) === 0 && stillPending === 0 && (
            <div style={{
              padding: 12, marginTop: 6,
              background: "#F0FDF4", border: "1px solid #BBF7D0",
              borderRadius: 6, color: "#166534", fontSize: 13,
            }}>
              ✓ All links checked — no pending clicks to recover. Webhooks are working correctly.
            </div>
          )}

          <div className="tlk-mo-foot" style={{ marginTop: 20, justifyContent: "flex-end" }}>
            <button className="tlk-btn-save" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Phase 5.1f: Pending Status modal (interactive, per-link flush) ──
// Shows count results from /count endpoint with rows for each link.
// Each row has a "Flush This Link" button that calls /:id/flush.
function PendingStatusModal({ data, busy, onFlushOne, onFlushAll, onRefresh, onClose }) {
  if (!data) return null;
  const links = data.per_link || [];
  const grandTotal = data.grand_total || 0;
  const hasAnyPending = grandTotal > 0;
  const hasAnyErrors  = links.some(l => l.errors && l.errors.length > 0);

  return (
    <div className="tlk-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tlk-mo-box" style={{ maxWidth: 780 }}>
        <div className="tlk-mo-hd">
          <span className="tlk-mo-title">
            Pending Clicks Status
            {hasAnyPending && <span style={{ marginLeft: 10, color: "#D97706" }}>
              ({grandTotal} total pending)
            </span>}
          </span>
          <button className="tlk-mo-close" onClick={onClose}>✕</button>
        </div>

        <div className="tlk-mo-body">


	  {/* Summary block — 3-column stat cards (HOTFIX 2) */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
            marginBottom: 16,
          }}>
            <div style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: "var(--bg-input, #F9FAFB)",
              border: "1px solid var(--border-color, #E5E7EB)",
              textAlign: "center",
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: "var(--text-muted, #6B7280)",
                textTransform: "uppercase", letterSpacing: 0.5,
                marginBottom: 4,
              }}>
                Scanned
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary, #111827)" }}>
                {data.scanned || 0}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted, #6B7280)", marginTop: 2 }}>
                link{(data.scanned || 0) !== 1 ? "s" : ""}
              </div>
            </div>

            <div style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: hasAnyPending ? "#FFFBEB" : "#F0FDF4",
              border: `1px solid ${hasAnyPending ? "#FCD34D" : "#BBF7D0"}`,
              textAlign: "center",
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: hasAnyPending ? "#92400E" : "#166534",
                textTransform: "uppercase", letterSpacing: 0.5,
                marginBottom: 4,
              }}>
                Total Pending
              </div>
              <div style={{
                fontSize: 22, fontWeight: 700,
                color: hasAnyPending ? "#D97706" : "#16A34A",
              }}>
                {grandTotal}
              </div>
              <div style={{
                fontSize: 11,
                color: hasAnyPending ? "#92400E" : "#166534",
                marginTop: 2,
              }}>
                click{grandTotal !== 1 ? "s" : ""}
              </div>
            </div>

            <div style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: "var(--bg-input, #F9FAFB)",
              border: "1px solid var(--border-color, #E5E7EB)",
              textAlign: "center",
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: "var(--text-muted, #6B7280)",
                textTransform: "uppercase", letterSpacing: 0.5,
                marginBottom: 4,
              }}>
                Duration
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary, #111827)" }}>
                {data.duration_ms || 0}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted, #6B7280)", marginTop: 2 }}>
                ms
              </div>
            </div>
          </div>


          {/* No pending case */}
          {!hasAnyPending && !hasAnyErrors && (
            <div style={{
              padding: 12, marginBottom: 12,
              background: "#F0FDF4", border: "1px solid #BBF7D0",
              borderRadius: 6, color: "#166534", fontSize: 13,
            }}>
              ✓ No pending clicks anywhere. All links are healthy.
            </div>
          )}

          {/* Per-link breakdown table */}
          {links.length > 0 && (
            <div style={{
              border: "1px solid var(--border-color, #e5e7eb)",
              borderRadius: 6, maxHeight: 400, overflow: "auto",
              marginBottom: 14,
            }}>
              <table className="tlk-pending-table">
                <thead>
                  <tr>
                    <th>Link</th>
                    <th style={{ textAlign: "right" }}>Click</th>
                    <th style={{ textAlign: "right" }}>Unsub</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map(link => {
                    const p = link.pending || { click: 0, unsub: 0, total: 0 };
                    const hasErrors = link.errors && link.errors.length > 0;
                    const isBusy = busy && busy.linkId === link.link_id;
                    return (
                      <tr key={link.link_id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{link.domain}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                            #{link.link_id} · {link.click_path} / {link.unsub_path}
                          </div>
                          {hasErrors && (
                            <div style={{ fontSize: 11, color: "#DC2626", marginTop: 3 }}>
                              ⚠ {link.errors.join(", ")}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: "right", color: p.click > 0 ? "#D97706" : "var(--text-muted)" }}>
                          {p.click}
                        </td>
                        <td style={{ textAlign: "right", color: p.unsub > 0 ? "#D97706" : "var(--text-muted)" }}>
                          {p.unsub}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 600,
                                     color: p.total > 0 ? "#D97706" : "var(--text-muted)" }}>
                          {p.total}
                        </td>
                        <td>
                          {p.total > 0 ? (
                            <button
                              className="tlk-btn-flush-row"
                              disabled={isBusy}
                              onClick={() => onFlushOne(link.link_id)}>
                              {isBusy ? "..." : "Flush"}
                            </button>
                          ) : (
                            <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer actions */}
          <div className="tlk-mo-foot" style={{ justifyContent: "space-between" }}>
            <button className="tlk-btn-cancel" onClick={onRefresh} disabled={!!busy}>
              ↻ Refresh
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="tlk-btn-cancel" onClick={onClose}>Close</button>
              {hasAnyPending && (
                <button
                  className="tlk-btn-save"
                  disabled={!!busy}
                  onClick={onFlushAll}>
                  {busy && busy.all ? "Flushing All…" : `Flush All (${grandTotal})`}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//   MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
export default function Links() {
  const { user, isRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isRole && isRole("admin");

  const [search,     setSearch]     = useState("");
  const [statusSel,  setStatusSel]  = useState("all");
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(25);
  const [sortCol,    setSortCol]    = useState("created_at");
  const [sortDir,    setSortDir]    = useState("desc");

  const [links,      setLinks]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  const [confirm,    setConfirm]    = useState(null);
  const [toast,      setToast]      = useState("");
  const toastTimer = useRef(null);

  // ── Flush (5.1e) state ───────────────────────────────────────────────
  const [flushBusy,   setFlushBusy]   = useState(false);
  const [flushResult, setFlushResult] = useState(null);

  // ── Phase 5.1f: Pending status state ─────────────────────────────────
  const [pendingBusy,   setPendingBusy]   = useState(false);   // initial check
  const [pendingData,   setPendingData]   = useState(null);    // count results
  const [perLinkBusy,   setPerLinkBusy]   = useState(null);    // { linkId } or { all: true }

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 3500);
  };

  const fetchLinks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const params = { page, pageSize };
      if (statusSel !== "all") params.status = statusSel;
      if (statusSel === "soft_deleted" && isAdmin) {
        params.include_soft_deleted = "1";
      }
      const res = await axios.get(`${API}/tracking-links`, { params });
      let rows = res.data.links || [];

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        rows = rows.filter(r =>
          (r.domain || "").toLowerCase().includes(q) ||
          (r.click_path || "").toLowerCase().includes(q) ||
          (r.unsub_path || "").toLowerCase().includes(q) ||
          (r.offer_name || "").toLowerCase().includes(q)
        );
      }

      rows.sort((a, b) => {
        let av = a[sortCol], bv = b[sortCol];
        if (av == null) av = "";
        if (bv == null) bv = "";
        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ?  1 : -1;
        return 0;
      });

      setLinks(rows);
      setTotal(res.data.total || rows.length);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || "Failed to fetch tracking links.");
    } finally {
      if (!silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusSel, search, sortCol, sortDir, isAdmin]);

  useEffect(() => {
    const t = setTimeout(() => { fetchLinks(); }, search.trim() ? 250 : 0);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusSel, page, pageSize, sortCol, sortDir]);

  useEffect(() => { setPage(1); }, [statusSel, search]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "click_count" || col === "unsub_count" || col === "created_at" ? "desc" : "asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span style={{opacity:0.3,marginLeft:3}}>↕</span>;
    return <span style={{marginLeft:3,color:"var(--sidebar-active-color)"}}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  // ── Lifecycle actions ────────────────────────────────────────────────
  const doDisable = async (link) => {
    setConfirm(c => ({ ...c, busy: true }));
    try {
      await axios.post(`${API}/tracking-links/${link.id}/disable`);
      showToast("Link disabled. Visitors will now see 410 Gone.");
      setConfirm(null);
      fetchLinks(true);
    } catch (err) {
      showToast(`Disable failed: ${err.response?.data?.message || err.message}`);
      setConfirm(c => ({ ...c, busy: false }));
    }
  };

  const doEnable = async (link) => {
    setConfirm(c => ({ ...c, busy: true }));
    try {
      await axios.post(`${API}/tracking-links/${link.id}/enable`);
      showToast("Link re-enabled. Webhook tracking resumed.");
      setConfirm(null);
      fetchLinks(true);
    } catch (err) {
      showToast(`Enable failed: ${err.response?.data?.message || err.message}`);
      setConfirm(c => ({ ...c, busy: false }));
    }
  };

  const doDelete = async (link) => {
    setConfirm(c => ({ ...c, busy: true }));
    try {
      await axios.delete(`${API}/tracking-links/${link.id}`);
      showToast("Link soft-deleted. It will be permanently erased after 30 days.");
      setConfirm(null);
      fetchLinks(true);
    } catch (err) {
      showToast(`Delete failed: ${err.response?.data?.message || err.message}`);
      setConfirm(c => ({ ...c, busy: false }));
    }
  };

  // ── 5.1e: Flush All Pending ──────────────────────────────────────────
  const doFlushPending = async () => {
    setFlushBusy(true);
    setFlushResult(null);
    try {
      const res = await axios.post(`${API}/tracking-links/flush`);
      const data = res.data || {};
      setFlushResult(data);
      fetchLinks(true);
      if ((data.flushed || 0) > 0) {
        showToast(`Recovered ${data.flushed} pending click${data.flushed !== 1 ? "s" : ""}.`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`Flush failed: ${msg}`);
      setFlushResult({
        success: false,
        scanned: 0, flushed: 0, still_pending: 0,
        errors: [msg],
      });
    } finally {
      setFlushBusy(false);
    }
  };

  // ── Phase 5.1f: Check Pending Status ─────────────────────────────────
  const doCheckPending = async () => {
    setPendingBusy(true);
    setPendingData(null);
    try {
      const res = await axios.post(`${API}/tracking-links/count`);
      setPendingData(res.data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`Check failed: ${msg}`);
      setPendingData({
        success: false,
        scanned: 0, grand_total: 0,
        per_link: [],
        errors: [msg],
      });
    } finally {
      setPendingBusy(false);
    }
  };

  // ── Phase 5.1f: Flush ONE link (from modal) ──────────────────────────
  const doFlushOneLink = async (linkId) => {
    setPerLinkBusy({ linkId });
    try {
      const res = await axios.post(`${API}/tracking-links/${linkId}/flush`);
      const data = res.data || {};
      showToast(`Recovered ${data.flushed || 0} click${(data.flushed || 0) !== 1 ? "s" : ""} for link #${linkId}.`);

      // Update the modal data — set this link's pending to 0 (or recheck)
      if (pendingData && pendingData.per_link) {
        const updatedLinks = pendingData.per_link.map(l => {
          if (l.link_id === linkId) {
            return {
              ...l,
              pending: {
                click: data.per_path?.find(p => p.link_type === "click")?.still_pending || 0,
                unsub: data.per_path?.find(p => p.link_type === "unsub")?.still_pending || 0,
                total: data.still_pending || 0,
              },
            };
          }
          return l;
        });
        const newGrandTotal = updatedLinks.reduce((sum, l) => sum + (l.pending?.total || 0), 0);
        setPendingData({ ...pendingData, per_link: updatedLinks, grand_total: newGrandTotal });
      }

      fetchLinks(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`Flush failed for link #${linkId}: ${msg}`);
    } finally {
      setPerLinkBusy(null);
    }
  };

  // ── Phase 5.1f: Flush All (from modal) ───────────────────────────────
  const doFlushAllFromModal = async () => {
    setPerLinkBusy({ all: true });
    try {
      const res = await axios.post(`${API}/tracking-links/flush`);
      const data = res.data || {};
      showToast(`Recovered ${data.flushed || 0} pending click${(data.flushed || 0) !== 1 ? "s" : ""}.`);
      // Re-check counts after flush
      const recheck = await axios.post(`${API}/tracking-links/count`);
      setPendingData(recheck.data);
      fetchLinks(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`Flush failed: ${msg}`);
    } finally {
      setPerLinkBusy(null);
    }
  };

  // ── Confirm modal builders ───────────────────────────────────────────
  const openDisable = (link) => setConfirm({
    kind: "disable", link, busy: false,
    title: "Disable this link?",
    body: (
      <div>
        <p style={{ margin: "0 0 12px" }}>
          Visitors clicking this link will see <strong>410 Gone</strong> instead of the redirect.
        </p>
        <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.6 }}>
          <li>Path stays reserved (you can re-enable instantly).</li>
          <li>Webhook tracking stops for new visitors.</li>
          <li>If left disabled for <strong>30 days</strong>, the link will be auto-promoted to soft-deleted.</li>
        </ul>
      </div>
    ),
    confirmLabel: "Yes, Disable",
    confirmDanger: false,
  });

  const openEnable = (link) => setConfirm({
    kind: "enable", link, busy: false,
    title: "Re-enable this link?",
    body: (
      <div>
        Re-enabling will restore the redirect on <strong>{link.domain}</strong>. Visitors will be redirected normally again, webhook tracking will resume, and the 30-day disable timer will reset.
      </div>
    ),
    confirmLabel: "Yes, Enable",
    confirmDanger: false,
  });

  const openDelete = (link) => setConfirm({
    kind: "delete", link, busy: false,
    title: "Delete this link?",
    body: (
      <div>
        <p style={{ margin: "0 0 10px", color: "#7f1d1d" }}>
          <strong>This soft-deletes the link.</strong>
        </p>
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.6 }}>
          <li>PHP files will be removed from hosting.</li>
          <li>The link becomes invisible to everyone.</li>
          <li>After <strong>30 days</strong> as soft-deleted, the database row and all click history will be permanently erased.</li>
        </ul>
      </div>
    ),
    confirmLabel: "Yes, Delete",
    confirmDanger: true,
  });

  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === "disable") return doDisable(confirm.link);
    if (confirm.kind === "enable")  return doEnable(confirm.link);
    if (confirm.kind === "delete")  return doDelete(confirm.link);
  };

  const counts = {
    all:          total,
    active:       links.filter(l => l.status === "active").length,
    disabled:     links.filter(l => l.status === "disabled").length,
    soft_deleted: links.filter(l => l.status === "soft_deleted").length,
  };

  const pageNumbers = totalPages > 1 ? buildPageNumbers(page, totalPages) : [];

  return (
    <div className="layout">
      <Sidebar active="links"/>
      <div className="layout-main tlk-page">
        <Header
          title="Tracking Links"
          subtitle={`${total} link${total !== 1 ? "s" : ""}${isAdmin ? " (admin view: all users)" : ""}`}
          userName={user?.name}
        />

        <div className="content tlk-content">

          {/* Info banner */}
          <div className="tlk-info-banner">
            <span className="tlk-info-icon">🔗</span>
            <span>
              Manage branded redirects created via Offer Details → Tracking Links.
              Click <strong>Disable</strong> to pause (returns 410 Gone),
              {" "}<strong>Enable</strong> to resume,
              {isAdmin && <> or <strong>Delete</strong> to soft-delete (admin only)</>}.
              {isAdmin && <> Use <strong>Check Pending</strong> to inspect webhook backlog or <strong>Flush Pending Clicks</strong> to recover them.</>}
            </span>
          </div>

          {/* Stats / filter tabs */}
          <div className="tlk-stats">
            {[
              { key: "all",          label: "All" },
              { key: "active",       label: "Active" },
              { key: "disabled",     label: "Disabled" },
              ...(isAdmin ? [{ key: "soft_deleted", label: "Soft-deleted" }] : []),
            ].map(s => (
              <div key={s.key}
                className={`tlk-stat ${statusSel === s.key ? "active" : ""} ${s.key === "soft_deleted" ? "tlk-stat-danger" : ""}`}
                onClick={() => setStatusSel(s.key)}>
                <span className="tlk-stat-num">{s.key === "all" ? total : counts[s.key]}</span>
                <span className="tlk-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="tlk-toolbar">
            <div className="tlk-search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search by domain, path, or offer name…"
                value={search}
                onChange={e => setSearch(e.target.value)}/>
              {search && <button className="tlk-search-clear" onClick={() => setSearch("")}>✕</button>}
            </div>

            {/* Phase 5.1f: Check Pending button (admin only) */}
            {isAdmin && (
              <button
                className="tlk-btn-check"
                onClick={doCheckPending}
                disabled={pendingBusy}
                title="Check how many clicks are queued in .pending files on hosting">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                     style={{ animation: pendingBusy ? "tlk-spin 1s linear infinite" : "none" }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {pendingBusy ? "Checking…" : "Check Pending"}
              </button>
            )}

            {/* 5.1e: Flush All button (admin only) */}
            {isAdmin && (
              <button
                className="tlk-btn-flush"
                onClick={doFlushPending}
                disabled={flushBusy}
                title="Recover any clicks queued in .pending files on hosting (rare webhook failures)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                     style={{ animation: flushBusy ? "tlk-spin 1s linear infinite" : "none" }}>
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                {flushBusy ? "Flushing…" : "Flush Pending Clicks"}
              </button>
            )}

            <button className="tlk-refresh-btn" onClick={() => fetchLinks()} title="Refresh list">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                   style={{ animation: loading ? "tlk-spin 1s linear infinite" : "none" }}>
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              Refresh
            </button>
          </div>

          {error && <div className="tlk-error">⚠ {error}</div>}

          {/* Table card */}
          <div className="tlk-table-wrap">
            <div className="tlk-table-head">
              <span className="tlk-count">
                {loading ? "Loading…" : `${total} link${total !== 1 ? "s" : ""}${statusSel !== "all" ? ` · ${statusSel}` : ""}`}
              </span>
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text-muted)"}}>
                <span>Show</span>
                <select className="tlk-pg-size-sel" value={pageSize}
                  onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }}>
                  {PG_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span>per page</span>
              </div>
            </div>

            <div className="tlk-scroll-area">
              <table className="tlk-table">
                <thead>
                  <tr>
                    <th className="sortable th-domain" onClick={() => handleSort("domain")}>
                      Domain<SortIcon col="domain"/>
                    </th>
                    <th className="th-paths">Click / Unsub Path</th>
                    <th className="sortable th-offer" onClick={() => handleSort("offer_name")}>
                      Offer<SortIcon col="offer_name"/>
                    </th>
                    <th className="sortable th-counts th-right" onClick={() => handleSort("click_count")}>
                      Clicks / Unsubs<SortIcon col="click_count"/>
                    </th>
                    <th className="sortable th-last" onClick={() => handleSort("last_clicked_at")}>
                      Last Clicked<SortIcon col="last_clicked_at"/>
                    </th>
                    <th className="sortable th-status" onClick={() => handleSort("status")}>
                      Status<SortIcon col="status"/>
                    </th>
                    <th className="th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="tlk-td-loading"><div className="tlk-spinner"/>Fetching links…</td></tr>
                  ) : links.length === 0 ? (
                    <tr><td colSpan={7} className="tlk-td-empty">
                      {search.trim()
                        ? `No links match "${search.trim()}". Try clearing the search.`
                        : statusSel === "all"
                          ? "No tracking links yet. Create one from any Offer Details page → Tracking Links button."
                          : `No ${statusSel} links found.`}
                    </td></tr>
                  ) : (
                    links.map(link => {
                      const isActive   = link.status === "active";
                      const isDisabled = link.status === "disabled";
                      const isDeleted  = link.status === "soft_deleted";
                      const clickUrl   = link.click_url || `http://${link.domain}${link.click_path}/`;

                      return (
                        <tr key={link.id} className={isDeleted ? "tlk-row-deleted" : ""}>
                          <td className="td-domain">
                            <a href={`http://${link.domain}`} target="_blank" rel="noopener noreferrer"
                               className="tlk-domain-link" title={`Open ${link.domain}`}>
                              {link.domain}
                            </a>
                            <div className="tlk-row-meta">
                              ID: {link.id} · {link.hosting_type}
                            </div>
                          </td>
                          <td className="td-paths">
                            <div className="tlk-path-row">
                              <span className="tlk-path-tag tlk-path-click">click</span>
                              <code className="tlk-path-code">{link.click_path}</code>
                            </div>
                            <div className="tlk-path-row" style={{ marginTop: 4 }}>
                              <span className="tlk-path-tag tlk-path-unsub">unsub</span>
                              <code className="tlk-path-code">{link.unsub_path}</code>
                            </div>
                          </td>
                          <td className="td-offer">
                            {link.offer_id ? (
                              <span
                                className="tlk-offer-link"
                                onClick={() => navigate(`/offers`)}
                                title="Go to offers list">
                                {link.offer_name || `Offer #${link.offer_id}`}
                              </span>
                            ) : (
                              <span className="td-dash">—</span>
                            )}
                          </td>
                          <td className="td-counts">
                            <div><strong>{link.click_count || 0}</strong> click{(link.click_count || 0) !== 1 ? "s" : ""}</div>
                            <div className="tlk-unsub-count">{link.unsub_count || 0} unsub{(link.unsub_count || 0) !== 1 ? "s" : ""}</div>
                          </td>
                          <td className="td-last">
                            {link.last_clicked_at ? (
                              <>
                                <div>{edtDateTime(link.last_clicked_at)}</div>
                                <div className="tlk-edt-tag">EDT</div>
                              </>
                            ) : <span className="td-dash">Never</span>}
                          </td>
                          <td className="td-status">
                            <StatusPill status={link.status}/>
                          </td>
                          <td className="td-actions">
                            <div className="tlk-action-row">
                              <button
                                className="tlk-btn tlk-btn-copy"
                                onClick={() => copyToClipboard(clickUrl, () => showToast("Click URL copied to clipboard."))}
                                title="Copy click URL">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                </svg>
                                Copy
                              </button>

                              {isActive && (
                                <button
                                  className="tlk-btn tlk-btn-disable"
                                  onClick={() => openDisable(link)}
                                  title="Pause this link (returns 410 Gone)">
                                  ⏸ Disable
                                </button>
                              )}

                              {isDisabled && (
                                <button
                                  className="tlk-btn tlk-btn-enable"
                                  onClick={() => openEnable(link)}
                                  title="Resume this link">
                                  ▶ Enable
                                </button>
                              )}

                              {isAdmin && !isDeleted && (
                                <button
                                  className="tlk-btn tlk-btn-delete"
                                  onClick={() => openDelete(link)}
                                  title="Soft-delete (admin only)">
                                  🗑
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="tlk-pagination">
                <span className="tlk-pg-info">{total.toLocaleString()} Total</span>
                <div className="tlk-pg-btns">
                  <button className="tlk-pg-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                  <button className="tlk-pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  {pageNumbers.map(p => (
                    <button key={p} className={`tlk-pg-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="tlk-pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                  <button className="tlk-pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.confirmLabel}
          confirmDanger={confirm.confirmDanger}
          busy={confirm.busy}
          onConfirm={runConfirm}
          onClose={() => !confirm.busy && setConfirm(null)}
        />
      )}

      {flushResult && (
        <FlushResultModal
          result={flushResult}
          onClose={() => setFlushResult(null)}
        />
      )}

      {pendingData && (
        <PendingStatusModal
          data={pendingData}
          busy={perLinkBusy}
          onFlushOne={doFlushOneLink}
          onFlushAll={doFlushAllFromModal}
          onRefresh={doCheckPending}
          onClose={() => setPendingData(null)}
        />
      )}

      {toast && <div className="tlk-toast">✅ {toast}</div>}
    </div>
  );
}
